import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MapPickerDialogData, MapPickerResult } from '../../../services/mapPickerService-service';

@Component({
  selector: 'app-map-picker-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    MatTooltipModule,
    GoogleMapsModule,
  ],
  templateUrl: './map-picker-dialog-component.html',
  styleUrls: ['./map-picker-dialog-component.scss'],
})
export class MapPickerDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('map') mapRef?: GoogleMap;
  @ViewChild('streetViewContainer') streetViewContainerRef?: ElementRef<HTMLDivElement>;

  mode: 'view' | 'search';
  center: google.maps.LatLngLiteral;
  markerPosition: google.maps.LatLngLiteral | null = null;
  address = '';
  zoom: number;
  searchQuery = '';
  latInput: number | null = null;
  lngInput: number | null = null;
  showStreetView = false;

  // Bounding box aproximado de la ciudad de Puno, Perú.
  // Ajusta estos valores si necesitas cubrir un área más amplia (p.ej. zonas periurbanas).
  private readonly punoBounds: google.maps.LatLngBoundsLiteral = {
    south: -15.90,
    west: -70.08,
    north: -15.78,
    east: -69.95,
  };

  mapOptions: google.maps.MapOptions = {
    streetViewControl: true,
    fullscreenControl: false,
    mapTypeControl: true,
    zoomControl: true,
    // Restringe el paneo/zoom del mapa al área de Puno (no estricto, permite un pequeño margen).
    restriction: {
      latLngBounds: this.punoBounds,
      strictBounds: false,
    },
  };

  markerOptions: google.maps.MarkerOptions = {};

  private geocoder!: google.maps.Geocoder;
  private autocomplete?: google.maps.places.Autocomplete;
  private streetViewPanorama?: google.maps.StreetViewPanorama;
  // Centro por defecto: ciudad de Puno, Perú.
  private defaultCenter: google.maps.LatLngLiteral = { lat: -15.8402, lng: -70.0219 };

  constructor(
    private dialogRef: MatDialogRef<MapPickerDialogComponent, MapPickerResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: MapPickerDialogData
  ) {
    this.mode = data?.mode ?? 'search';
    this.zoom = data?.zoom ?? 16;
    this.markerOptions = { draggable: this.mode === 'search' };

    if (data?.lat != null && data?.lng != null) {
      this.center = { lat: data.lat, lng: data.lng };
      this.markerPosition = { lat: data.lat, lng: data.lng };
      this.latInput = data.lat;
      this.lngInput = data.lng;
    } else {
      this.center = this.defaultCenter;
    }
    this.address = data?.address ?? '';
    this.searchQuery = this.address;
  }

  ngOnInit(): void {
    this.geocoder = new google.maps.Geocoder();
  }

  ngAfterViewInit(): void {
    if (this.mode === 'search' && this.searchInputRef) {
      const bounds = new google.maps.LatLngBounds(
        { lat: this.punoBounds.south, lng: this.punoBounds.west },
        { lat: this.punoBounds.north, lng: this.punoBounds.east }
      );

      this.autocomplete = new google.maps.places.Autocomplete(this.searchInputRef.nativeElement, {
        fields: ['geometry', 'formatted_address', 'name'],
        bounds,
        strictBounds: true, // fuerza a Google a devolver solo resultados dentro del bounding box
        componentRestrictions: { country: 'pe' },
      });
      this.autocomplete.addListener('place_changed', () => this.onPlaceChanged());
    }
    if (this.markerPosition) {
      setTimeout(() => this.mapRef?.panTo(this.markerPosition!));
    }
  }

  ngOnDestroy(): void {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (this.mode !== 'search' || !event.latLng) return;
    const pos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    this.setMarker(pos);
    this.reverseGeocode(pos);
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;
    const pos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    this.setMarker(pos);
    this.reverseGeocode(pos);
  }

  onPlaceChanged(): void {
    const place = this.autocomplete?.getPlace();
    if (!place?.geometry?.location) return;
    const pos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    this.address = place.formatted_address ?? place.name ?? '';
    this.searchQuery = this.address;
    this.setMarker(pos);
  }

  geocodeAddress(): void {
    if (!this.searchQuery?.trim()) return;
    const bounds = new google.maps.LatLngBounds(
      { lat: this.punoBounds.south, lng: this.punoBounds.west },
      { lat: this.punoBounds.north, lng: this.punoBounds.east }
    );

    this.geocoder.geocode(
      {
        address: this.searchQuery,
        bounds, // bias de resultados hacia Puno (el Geocoder no soporta filtro estricto por bounds)
        componentRestrictions: { country: 'PE' },
      },
      (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          const pos = { lat: loc.lat(), lng: loc.lng() };
          this.address = results[0].formatted_address;
          this.searchQuery = this.address;
          this.setMarker(pos);
        }
      }
    );
  }

  onManualCoordsChange(): void {
    if (this.latInput == null || this.lngInput == null) return;
    if (this.latInput < -90 || this.latInput > 90 || this.lngInput < -180 || this.lngInput > 180) return;
    const pos = { lat: this.latInput, lng: this.lngInput };
    this.setMarker(pos, false);
    this.reverseGeocode(pos);
  }

  onToggleStreetView(): void {
    if (this.showStreetView && this.markerPosition) {
      setTimeout(() => this.updateStreetView(this.markerPosition!));
    }
  }

  accept(): void {
    if (!this.markerPosition) return;
    this.dialogRef.close({
      lat: this.markerPosition.lat,
      lng: this.markerPosition.lng,
      address: this.address || undefined,
    });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private setMarker(pos: google.maps.LatLngLiteral, syncInputs = true): void {
    this.markerPosition = pos;
    this.center = pos;
    if (this.zoom < 16) this.zoom = 16;
    if (syncInputs) {
      this.latInput = pos.lat;
      this.lngInput = pos.lng;
    }
    this.mapRef?.panTo(pos);
    this.updateStreetView(pos);
  }

  private reverseGeocode(pos: google.maps.LatLngLiteral): void {
    this.geocoder.geocode({ location: pos }, (results, status) => {
      const formatted = status === 'OK' && results && results[0] ? results[0].formatted_address : '';
      this.address = formatted;
      // Refleja la dirección en el campo de búsqueda al mover/clickear el marker.
      this.searchQuery = formatted;
      if (this.searchInputRef) {
        this.searchInputRef.nativeElement.value = formatted;
      }
    });
  }

  private updateStreetView(pos: google.maps.LatLngLiteral): void {
    if (!this.showStreetView || !this.streetViewContainerRef) return;
    if (!this.streetViewPanorama) {
      this.streetViewPanorama = new google.maps.StreetViewPanorama(this.streetViewContainerRef.nativeElement, {
        position: pos,
        visible: true,
        addressControl: false,
      });
    } else {
      this.streetViewPanorama.setPosition(pos);
    }
  }
}